import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Clock, Plus, Sparkles } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/Breadcrumb";
import { getPostBySlug, blogPosts } from "@/data/blogPosts";
import { getServiceBySlug } from "@/data/services";
import { Button, Eyebrow, smoothScrollToId } from "@/components/kit";

const BlogPostPage = ({ slug }) => {
  const post = getPostBySlug(slug);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.3 });

  const jsonLd = post
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BlogPosting",
            headline: post.title,
            description: post.metaDescription,
            datePublished: post.dateISO,
            dateModified: post.dateISO,
            inLanguage: "nl-NL",
            mainEntityOfPage: `https://uppit.nl/blog/${post.slug}`,
            image: `https://uppit.nl${post.image}`,
            author: { "@type": "Person", name: "Tim Roosendaal", url: "https://uppit.nl/over-ons" },
            publisher: {
              "@type": "Organization",
              name: "UPPIT",
              url: "https://uppit.nl",
              logo: { "@type": "ImageObject", url: "https://uppit.nl/brand/uppit-logo-creme.png" },
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: post.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      }
    : null;

  useSeo({
    title: post ? post.metaTitle : "Artikel niet gevonden",
    description: post ? post.metaDescription : "Dit artikel bestaat niet.",
    path: post ? `/blog/${post.slug}` : "/blog",
    ogSlug: "page/blog",
    jsonLd,
    noindex: !post,
  });

  if (!post) {
    return (
      <main data-testid="blogpost-notfound" className="bg-ivory min-h-screen pt-40 pb-24 text-center px-6">
        <h1 className="font-display font-black text-4xl text-coal mb-4">Artikel niet gevonden</h1>
        <Button to="/blog" variant="primary" withArrow testid="blogpost-back">Naar de kennisbank</Button>
      </main>
    );
  }

  const related = (post.related || []).map(getPostBySlug).filter(Boolean);
  const service = post.relatedService ? getServiceBySlug(post.relatedService) : null;

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    smoothScrollToId(id);
  };

  return (
    <article data-testid={`blogpost-${post.slug}`} className="relative bg-ivory pt-28 lg:pt-32 pb-24">
      <motion.div
        style={{ scaleX: progress }}
        data-testid="blogpost-progress"
        className="fixed top-0 left-0 right-0 h-1 bg-electric origin-left z-[60]"
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Kennisbank", href: "/blog" },
            { label: post.title, href: `/blog/${post.slug}` },
          ]}
        />

        {/* Header */}
        <header className="mt-10 lg:mt-14 max-w-4xl">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="rounded-full bg-electric text-white text-[11px] font-semibold px-3.5 py-1.5">{post.category}</span>
            <span className="text-sm text-coal/50">{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-coal/20" />
            <span className="inline-flex items-center gap-1.5 text-sm text-coal/50"><Clock className="w-3.5 h-3.5" /> {post.readingTime} leestijd</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-black text-coal tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-balance"
          >
            {post.title}
          </motion.h1>
          <div className="mt-7 flex items-center gap-4">
            <img src="/team/tim-avatar.webp" alt="Tim Roosendaal" className="w-11 h-11 rounded-full object-cover border-2 border-electric/50" />
            <div>
              <div className="font-display font-bold text-coal text-sm leading-tight">Tim Roosendaal</div>
              <div className="text-xs text-coal/50">Oprichter UPPIT · online marketingstrateeg</div>
            </div>
          </div>
        </header>

        {/* Visual */}
        <div className="mt-10 rounded-[1.75rem] border border-ivory-400 bg-gradient-to-br from-ivory-100 to-white overflow-hidden relative h-56 lg:h-72">
          <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-contain p-6 drop-shadow-[0_28px_48px_rgba(0,56,255,0.18)]" />
        </div>

        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Main */}
          <div className="lg:col-span-8">
            {/* Takeaways */}
            <div data-testid="blogpost-takeaways" className="rounded-[1.5rem] border border-electric/20 bg-electric/5 p-7 lg:p-8 mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-electric" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric-700">In het kort</span>
              </div>
              <ul className="space-y-3">
                {post.takeaways.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-coal/80">
                    <CheckCircle2 className="w-5 h-5 text-electric shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sections */}
            <div className="space-y-12 lg:space-y-14">
              {post.sections.map((s, i) => (
                <motion.section
                  key={s.id}
                  id={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="scroll-mt-28"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-display font-bold text-2xl lg:text-3xl text-coal mb-6 leading-tight tracking-tight text-balance">
                    {s.h2}
                  </h2>
                  <div className="space-y-5">{s.content}</div>
                </motion.section>
              ))}
            </div>

            {/* FAQ */}
            <div className="mt-16">
              <Eyebrow>FAQ · Veelgestelde vragen</Eyebrow>
              <div className="mt-6 rounded-[1.5rem] border border-ivory-400 bg-white overflow-hidden shadow-card-light">
                {post.faqs.map((f, i) => (
                  <details key={i} data-testid={`blogpost-faq-${i}`} className="group border-b border-ivory-300 last:border-b-0">
                    <summary className="cursor-pointer flex items-start justify-between gap-4 list-none px-6 lg:px-8 py-6">
                      <span className="font-display font-bold text-lg text-coal leading-snug">{f.q}</span>
                      <Plus className="w-5 h-5 text-electric transition-transform duration-500 group-open:rotate-45 shrink-0 mt-1" />
                    </summary>
                    <div className="px-6 lg:px-8 pb-6 text-coal/65 leading-relaxed max-w-2xl">{f.a}</div>
                  </details>
                ))}
              </div>
            </div>

            {/* Author box */}
            <div className="mt-14 rounded-[1.5rem] border border-ivory-400 bg-white p-7 lg:p-8 shadow-card-light flex flex-col sm:flex-row items-start gap-5">
              <img src="/team/tim-avatar-2.webp" alt="Tim Roosendaal" className="w-16 h-16 rounded-full object-cover border-2 border-electric/50 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-coal/40 mb-1.5">Over de auteur</div>
                <div className="font-display font-bold text-lg text-coal">Tim Roosendaal</div>
                <p className="mt-2 text-sm text-coal/60 leading-relaxed max-w-xl">
                  Oprichter van UPPIT en marketingstrateeg. Helpt MKB'ers en ZZP'ers structureel groeien met AI-gedreven
                  marketing, SEO en websites die converteren. Schrijft zoals hij werkt: eerlijk, praktisch en zonder wollige taal.
                </p>
                <Link to="/over-ons" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-electric hover:underline underline-offset-4">
                  Meer over Tim <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-[1.5rem] border border-ivory-400 bg-white p-6 shadow-card-light">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-coal/40 mb-4">In dit artikel</div>
              <ol className="space-y-1">
                {post.sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={scrollTo(s.id)}
                      data-testid={`blogpost-toc-${s.id}`}
                      className="group flex items-baseline gap-3 py-1.5 text-sm text-coal/60 hover:text-electric transition-colors"
                    >
                      <span className="font-mono text-[10px] text-electric/70">{String(i + 1).padStart(2, "0")}</span>
                      <span className="leading-snug">{s.h2}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-[1.5rem] bg-obsidian text-ivory p-7 relative overflow-hidden">
              <div className="glow-orb w-[260px] h-[260px] bg-electric/25 -top-14 -right-10" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric-300 mb-3">Gratis groeiadvies</div>
                <p className="text-white/70 leading-relaxed text-sm mb-5">
                  Weten waar jouw grootste kansen liggen? Doe de AI Growth Scan — 6 vragen, direct een persoonlijk plan.
                </p>
                <Button to="/ai-scan" variant="primary" testid="blogpost-cta-aiscan" className="w-full justify-center">
                  Start AI Growth Scan
                </Button>
              </div>
            </div>

            {service && (
              <Link
                to={`/diensten/${service.slug}`}
                data-testid="blogpost-related-service"
                className="group block rounded-[1.5rem] border border-ivory-400 bg-white p-6 shadow-card-light hover:border-electric/40 transition-all"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-coal/40 mb-2">Gerelateerde dienst</div>
                <div className="font-display font-bold text-coal group-hover:text-electric transition-colors flex items-center gap-2">
                  {service.title}
                  <ArrowUpRight className="w-4 h-4" />
                </div>
                <p className="mt-1.5 text-sm text-coal/55">{service.short}</p>
              </Link>
            )}
          </aside>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div className="mt-20 pt-12 border-t border-ivory-400">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-coal/40 mb-8">Lees ook</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`blogpost-related-${r.slug}`}
                  className="group flex items-center gap-5 rounded-[1.5rem] border border-ivory-400 bg-white p-5 lg:p-6 shadow-card-light hover:border-electric/40 hover:shadow-card-lift hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-20 h-20 rounded-xl bg-ivory-100 shrink-0 overflow-hidden relative">
                    <img src={r.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain p-2 drop-shadow-[0_10px_18px_rgba(0,56,255,0.15)]" />
                  </div>
                  <div>
                    <div className="text-[11px] text-electric font-semibold mb-1">{r.category}</div>
                    <div className="font-display font-bold text-coal leading-snug group-hover:text-electric transition-colors">{r.title}</div>
                    <div className="mt-1 text-xs text-coal/45 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.readingTime}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogPostPage;
